// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * BOM versions for a product type, and the import that creates them (design doc §7.3,
 * §8.2, §9.3, US-E-02…04).
 *
 * Import is two steps by design. Preflight uploads the file and returns a report — row
 * count, how many SN slots the critical lines expand to, and every problem found — without
 * writing a BOM. Commit then turns that validated batch into a draft. The split exists
 * because a BOM is the thing every later filing is measured against: a bad one does not
 * announce itself at import time, it announces itself weeks later as machines that cannot
 * be filed.
 *
 * Note that preflight answers 200 with a report even when the file is unusable. The upload
 * succeeded; the file is what has problems. Reporting that as an HTTP error would conflate
 * the two.
 *
 * Each row also says how many archives were filed against the version, and links to them.
 * That number decides what can be done with the row: a version nothing was filed against
 * can be deleted outright, while one in use can only be disabled (停用) — it stops being
 * offered for new filings, and every machine already built to it keeps citing it.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { NAME_MAX_LEN } from "@/api/constraints";
import { saveResponseAsFile } from "@/api/download";
import { api } from "@/api/endpoints";
import type { Bom, BomPreflight } from "@/api/types";
import { AddLabel } from "@/components/AddLabel";
import { BomPreflightReport } from "@/components/BomPreflightReport";
import { ErrorBanner } from "@/components/ErrorBanner";
import { IconButton } from "@/components/IconButton";
import { BanIcon, RestoreIcon, TrashIcon } from "@/components/icons";
import { Modal } from "@/components/Modal";
import { Pager } from "@/components/Pager";
import { ResizableTable } from "@/components/ResizableTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useCursorList } from "@/hooks/useCursorList";
import { usePermissions } from "@/hooks/usePermissions";

export function BomList() {
  const { t } = useTranslation();
  // Read by everyone; drafted, imported, stopped and deleted by an administrator (§12.4).
  const { canManageMasterData } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Also in the query string, so the product-type list can link straight to one type's
  // versions.
  const [params, setParams] = useSearchParams();
  const productTypeId = params.get("product_type_id") ?? "";
  const setProductTypeId = (value: string) =>
    setParams(value ? { product_type_id: value } : {}, { replace: true });
  const [preflight, setPreflight] = useState<BomPreflight | null>(null);
  // Kept so a workbook with several BOM sheets can be preflighted again on another one.
  const [uploaded, setUploaded] = useState<File | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [draftTypeId, setDraftTypeId] = useState("");
  const [draftName, setDraftName] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Bom | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const productTypes = useCursorList(["product-types", "all"], (cursor) =>
    api.productTypes.list({ cursor, limit: 200 }),
  );

  // No longer gated on a product type. The page used to open empty and stay empty until
  // one was chosen, which reads as a broken page rather than as a filter waiting to be set.
  // Its own key under the "boms" prefix. The filing and batch screens cache one page of a
  // type's BOMs as ["boms", type]; this is paged (infinite) data, a different shape, and
  // sharing the key made this page read theirs and crash on choosing that type.
  const boms = useCursorList(["boms", "pages", productTypeId], (cursor) =>
    api.boms.list(productTypeId, { cursor, limit: 50 }),
  );

  // The type name is resolved from the list the filter already loaded, rather than joined
  // server-side for a column that only matters while the list is unfiltered.
  const typeNames = new Map(productTypes.items.map((type) => [type.id, type.name]));

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["boms", "pages", productTypeId] });
  // Every BOM list, not only this one: the filing pages cache theirs under the same prefix,
  // and a version just disabled must drop out of their selectors too.
  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: ["boms"] });

  const disable = useMutation({
    mutationFn: (bomId: string) => api.boms.disable(bomId),
    onSuccess: invalidateAll,
  });
  const enable = useMutation({
    mutationFn: (bomId: string) => api.boms.enable(bomId),
    onSuccess: invalidateAll,
  });
  const remove = useMutation({
    mutationFn: (bomId: string) => api.boms.remove(bomId),
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidateAll();
    },
  });
  const closeDelete = () => {
    setPendingDelete(null);
    remove.reset();
  };

  const createDraft = useMutation({
    mutationFn: ({ typeId, name }: { typeId: string; name: string }) =>
      api.boms.create(typeId, name),
    // Straight into the new draft: it is empty, and the next thing to do is put lines in
    // it. Leaving the author on the list would show them a row they then have to find.
    onSuccess: (created) => navigate(`/boms/${created.id}`),
  });

  const downloadTemplate = useMutation({
    mutationFn: api.bomImports.template,
    onSuccess: (response) => saveResponseAsFile(response, "cybertron-bom-template.xlsx"),
  });

  const runPreflight = useMutation({
    mutationFn: ({ file, sheet }: { file: File; sheet?: string }) =>
      api.bomImports.preflight(productTypeId, file, sheet),
    onSuccess: setPreflight,
  });

  const commitImport = useMutation({
    mutationFn: ({ batchId, acknowledged }: { batchId: string; acknowledged: boolean }) =>
      api.bomImports.commit(batchId, acknowledged),
    onSuccess: async (created) => {
      setPreflight(null);
      setUploaded(null);
      if (fileInput.current) fileInput.current.value = "";
      await invalidate();
      // Straight to the draft: its version is typed in there before it can be published.
      navigate(`/boms/${created.bom_id}`);
    },
  });

  return (
    <section>
      <div className="page-head">
        <h1>{t("bom.title")}</h1>
        {/* Top right, where every list page keeps its "new" button. */}
        {canManageMasterData && (
          <button type="button" className="button-primary" onClick={() => setCreatingDraft(true)}>
            <AddLabel>{t("bom.newDraft")}</AddLabel>
          </button>
        )}
      </div>

      <div className="filters">
        <select
          aria-label={t("archive.productType")}
          value={productTypeId}
          onChange={(event) => {
            setProductTypeId(event.target.value);
            setPreflight(null);
          }}
        >
          <option value="">{t("archive.productType")}</option>
          {productTypes.items
            .filter((type) => type.has_bom)
            .map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
        </select>
        {/* The file input itself is never shown. A bare one renders as the browser's own
            「选择文件 未选择任何文件」 control, which looks nothing like the rest of the
            page and states a fact nobody needs. The button is the control; the input is
            plumbing, so it is hidden from assistive technology and from the tab order —
            the button is what both reach. */}
        <input
          ref={fileInput}
          type="file"
          className="visually-hidden"
          tabIndex={-1}
          aria-hidden="true"
          accept=".xlsx,.csv"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setUploaded(file);
              runPreflight.mutate({ file });
            }
            // Cleared so choosing the same file twice fires a change event the second time
            // — which is exactly what someone does after fixing the spreadsheet in place.
            event.target.value = "";
          }}
        />
        {canManageMasterData && (
          <button
            type="button"
            disabled={!productTypeId || runPreflight.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {runPreflight.isPending ? t("common.loading") : t("bom.import")}
          </button>
        )}
        {/* Not gated on a product type: someone preparing a BOM offline has no reason to
            pick one first, and the template is the same for every type. */}
        <button
          type="button"
          disabled={downloadTemplate.isPending}
          onClick={() => downloadTemplate.mutate()}
        >
          {t("bom.downloadTemplate")}
        </button>
      </div>
      {canManageMasterData && (
        <p className="hint">{productTypeId ? t("bom.templateHint") : t("bom.importNeedsType")}</p>
      )}

      <ErrorBanner
        error={
          runPreflight.error ??
          commitImport.error ??
          downloadTemplate.error ??
          disable.error ??
          enable.error
        }
      />

      {preflight && (
        <BomPreflightReport
          // A fresh report starts unacknowledged: ticking the box was about the last one.
          key={preflight.batch_id}
          report={preflight}
          committing={commitImport.isPending}
          onCommit={(acknowledged) =>
            commitImport.mutate({ batchId: preflight.batch_id, acknowledged })
          }
          onChooseSheet={(sheet) => uploaded && runPreflight.mutate({ file: uploaded, sheet })}
        />
      )}

      {creatingDraft && (
        <Modal
          title={t("bom.newDraft")}
          onClose={() => setCreatingDraft(false)}
          onSubmit={() =>
            createDraft.mutate({ typeId: draftTypeId || productTypeId, name: draftName })
          }
          footer={
            <>
              <button
                type="submit"
                className="button-primary"
                disabled={!(draftTypeId || productTypeId) || createDraft.isPending}
              >
                {t("common.create")}
              </button>
              <button type="button" onClick={() => setCreatingDraft(false)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p className="hint">{t("bom.newDraftHint")}</p>
          <div className="form-row">
            <label className="required" htmlFor="draft-product-type">
              {t("archive.productType")}
            </label>
            <select
              id="draft-product-type"
              required
              value={draftTypeId || productTypeId}
              onChange={(event) => setDraftTypeId(event.target.value)}
            >
              <option value="">—</option>
              {productTypes.items
                .filter((type) => type.has_bom && type.status === "ACTIVE")
                .map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="draft-name">{t("bom.name")}</label>
            <input
              id="draft-name"
              maxLength={NAME_MAX_LEN}
              value={draftName}
              placeholder={t("bom.namePlaceholder")}
              onChange={(event) => setDraftName(event.target.value)}
            />
          </div>
          <ErrorBanner error={createDraft.error} />
        </Modal>
      )}

      {pendingDelete && (
        <Modal
          title={t("bom.deleteTitle")}
          onClose={closeDelete}
          onSubmit={() => remove.mutate(pendingDelete.id)}
          footer={
            <>
              <button type="submit" className="button-danger" disabled={remove.isPending}>
                {t("common.delete")}
              </button>
              <button type="button" onClick={closeDelete}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("bom.deleteConfirm", { version: pendingDelete.version ?? t("bom.unnumbered") })}</p>
          {/* Someone may have filed against it since the list loaded; the server's refusal
              is read here, where the decision is. */}
          <ErrorBanner error={remove.error} />
        </Modal>
      )}

      <ResizableTable storageKey="bom-list">
        <thead>
          <tr>
            <th>{t("bom.version")}</th>
            <th>{t("bom.name")}</th>
            {!productTypeId && <th>{t("archive.productType")}</th>}
            <th>{t("bom.status")}</th>
            <th>{t("bom.rows")}</th>
            <th>{t("bom.criticalItems")}</th>
            <th>{t("bom.requiredSn")}</th>
            <th>{t("bom.archiveCount")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {boms.items.map((bom) => (
            <tr key={bom.id}>
              <td>
                <Link to={`/boms/${bom.id}`}>{bom.version ?? t("bom.unnumbered")}</Link>
              </td>
              <td>{bom.name ?? <span className="muted">—</span>}</td>
              {!productTypeId && (
                <td>{typeNames.get(bom.product_type_id) ?? bom.product_type_id}</td>
              )}
              <td>
                <StatusBadge status={bom.status} />
                {bom.disabled_at && (
                  <>
                    {" "}
                    <StatusBadge status="DISABLED" />
                  </>
                )}
              </td>
              <td>{bom.row_count ?? "—"}</td>
              <td>{bom.critical_item_count ?? "—"}</td>
              <td>{bom.required_sn_count ?? "—"}</td>
              <td>
                {/* Zero is not a link, for the same reason as on the product-type list: a
                    filtered list of nothing is a dead end. */}
                {bom.archive_count > 0 ? (
                  <Link to={`/archives?source_bom_id=${bom.id}`}>{bom.archive_count}</Link>
                ) : (
                  <span className="muted">0</span>
                )}
              </td>
              <td className="row-actions">
                {!canManageMasterData ? null : bom.disabled_at ? (
                  <IconButton
                    label={t("bom.enable")}
                    icon={<RestoreIcon />}
                    disabled={enable.isPending}
                    onClick={() => enable.mutate(bom.id)}
                  />
                ) : (
                  <IconButton
                    label={t("bom.disable")}
                    icon={<BanIcon />}
                    disabled={disable.isPending}
                    onClick={() => disable.mutate(bom.id)}
                  />
                )}
                {/* Greyed where the server would refuse it. A version in use is disabled,
                    not deleted — the archives built to it still cite it. */}
                {canManageMasterData && (
                  <IconButton
                    label={t("common.delete")}
                    icon={<TrashIcon />}
                    danger
                    disabled={bom.archive_count > 0}
                    disabledReason={t("bom.deleteBlocked", { count: bom.archive_count })}
                    onClick={() => setPendingDelete(bom)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
      {boms.items.length === 0 && !boms.isLoading && (
        <p className="muted">{productTypeId ? t("bom.emptyForType") : t("common.empty")}</p>
      )}
      <Pager
        hasNextPage={boms.hasNextPage}
        isFetchingNextPage={boms.isFetchingNextPage}
        onMore={() => void boms.fetchNextPage()}
      />
    </section>
  );
}
